/**
 * Unified widget config envelope (`instances[]` + shared root) helpers.
 */

import { ECOMMERCE_TRIGGER_CONFIG_KEYS } from './ecommerceTriggerConfig.js'
import { mergeSlideOutPanelLayout } from './slideOutPanelLayout.js'

function isPlainObject(v) {
  return v != null && typeof v === 'object' && !Array.isArray(v)
}

/** Legacy master-key surfaces use -1 when the current URL has no launch target. */
function isUnsetLaunchConversationId(value) {
  return value === -1 || value === '-1'
}

function resolveLaunchConversationIdForMerge(instance, base) {
  const inst = instance?.launchConversationId
  if (inst != null && inst !== '' && !isUnsetLaunchConversationId(inst)) {
    return inst
  }
  if (isUnsetLaunchConversationId(inst)) {
    const baseLaunch = base?.launchConversationId
    if (baseLaunch != null && baseLaunch !== '' && !isUnsetLaunchConversationId(baseLaunch)) {
      return baseLaunch
    }
    const menu = instance?.defaultMenuConversationId ?? base?.defaultMenuConversationId
    if (menu != null && menu !== '' && !isUnsetLaunchConversationId(menu)) {
      return menu
    }
    return undefined
  }
  return base?.launchConversationId
}

export function mergeEnvelopeWithInstance(envelope, instance) {
  if (!envelope || typeof envelope !== 'object') return envelope
  if (!instance || typeof instance !== 'object') return envelope

  const {
    instances: _i,
    scripts: _scripts,
    version: _version,
    deviceType: _deviceType,
    loaderVersion: _loaderVersion,
    ...rest
  } = envelope
  const base = { ...rest }

  const theme =
    instance.kind === 'injected'
      ? mergeThemeForInjectedInstance(base.theme, instance.theme)
      : mergeDeep(
          isPlainObject(base.theme) ? base.theme : {},
          isPlainObject(instance.theme) ? instance.theme : {},
        )

  const streaming = {
    ...(isPlainObject(base.streaming) ? base.streaming : {}),
    ...(isPlainObject(instance.streaming) ? instance.streaming : {}),
  }

  const publicChat = {
    ...(isPlainObject(base.public_chat) ? base.public_chat : {}),
    ...(isPlainObject(base.publicChat) ? base.publicChat : {}),
    ...(isPlainObject(instance.public_chat) ? instance.public_chat : {}),
    ...(isPlainObject(instance.publicChat) ? instance.publicChat : {}),
  }

  const out = {
    ...base,
    theme,
    streaming,
    public_chat: publicChat,
    ctas: instance.ctas ?? instance.activeCtas ?? base.ctas ?? base.activeCtas,
    leadForms:
      instance.leadForms ??
      instance.activeLeadForms ??
      base.leadForms ??
      base.activeLeadForms,
    activeCtas: instance.activeCtas ?? instance.ctas ?? base.activeCtas ?? base.ctas,
    activeLeadForms:
      instance.activeLeadForms ??
      instance.leadForms ??
      base.activeLeadForms ??
      base.leadForms,
    launchConversationId: resolveLaunchConversationIdForMerge(instance, base),
    triggerConfig:
      instance.triggerConfig != null ? instance.triggerConfig : base.triggerConfig,
    urlFilterTriggers:
      instance.urlFilterTriggers !== undefined
        ? instance.urlFilterTriggers
        : base.urlFilterTriggers,
  }

  if (instance.widgetInternalAgentId != null) {
    out.widgetInternalAgentId = instance.widgetInternalAgentId
  }
  if (instance.publicAgentId != null && instance.publicAgentId !== '') {
    out.publicAgentId = instance.publicAgentId
  }

  if (instance.kind != null && instance.kind !== '') {
    out.kind = instance.kind
  }

  const instancePassthrough = [
    'widgetType',
    'searchBoxType',
    'searchTexts',
    'slideOutConfig',
    'buttonText',
    'buttonColor',
    'buttonTextColor',
    'buttonBorderColor',
    'buttonBorderRadius',
    'buttonIcon',
    'buttonIconPosition',
    'buttonIconAnimation',
    'injectionSelector',
    'injectionType',
    'displayType',
    'orderStatus',
    'defaultMenuConversationId',
    'menuItems',
    'menuEcommerce',
    ...ECOMMERCE_TRIGGER_CONFIG_KEYS,
  ]
  for (const k of instancePassthrough) {
    if (instance[k] !== undefined) {
      out[k] = instance[k]
    }
  }

  return out
}

/**
 * Root envelope `theme.layout` is floating-centric (margins, corner anchor).
 * Injected mounts must not inherit those keys — only the instance layout
 * applies, plus the injected surface's own slide-out dimensions (canonical
 * backfills any slide-out key the instance left unset).
 */
function mergeThemeForInjectedInstance(baseTheme, instanceTheme) {
  const bt = isPlainObject(baseTheme) ? baseTheme : {}
  const it = isPlainObject(instanceTheme) ? instanceTheme : {}
  const baseLayout = isPlainObject(bt.layout) ? bt.layout : {}
  const instanceLayout = isPlainObject(it.layout) ? it.layout : {}
  const { layout: _baseLayout, ...baseRest } = bt
  const { layout: _instanceLayout, ...instanceRest } = it
  return {
    ...mergeDeep(baseRest, instanceRest),
    layout: mergeSlideOutPanelLayout(baseLayout, instanceLayout),
  }
}

function mergeDeep(a, b) {
  const out = { ...a }
  for (const [k, v] of Object.entries(b)) {
    if (isPlainObject(v) && isPlainObject(out[k])) {
      out[k] = mergeDeep(out[k], v)
    } else {
      out[k] = v
    }
  }
  return out
}

/**
 * Light-DOM mount hosts are often empty `div`s (widget UI lives in shadow DOM).
 * Shopify Dawn and similar themes hide `div:empty { display: none }`, which
 * removes the host and everything inside its shadow root.
 */
export function prepareWidgetMountHost(host) {
  if (!host || host.nodeType !== 1) return host

  host.classList.add('ometrics-widget-mount-host')

  if (host.childNodes.length === 0) {
    const sentinel = document.createElement('span')
    sentinel.className = 'ometrics-widget-mount-host__sentinel'
    sentinel.setAttribute('aria-hidden', 'true')
    // Any text node prevents `:empty` from matching the host.
    sentinel.textContent = '\u200b'
    sentinel.style.cssText =
      'position:absolute;width:0;height:0;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;padding:0;margin:0;pointer-events:none;'
    host.appendChild(sentinel)
  }

  if (!host.style.display) {
    host.style.display = 'block'
  }

  return host
}

function createFloatingHost(instanceId) {
  const node = document.createElement('div')
  node.id = `ometrics-ai-${instanceId}`
  document.body.appendChild(node)
  prepareWidgetMountHost(node)
  return node
}

function normalizeInjectionType(v) {
  const s = String(v ?? 'append').trim().toLowerCase()
  if (s === 'before' || s === 'after' || s === 'append' || s === 'prepend') {
    return s
  }
  return 'append'
}

/**
 * Creates a light-DOM host for the shadow widget and inserts it relative to
 * `target` per admin `injectionType`. Mounting on `target` directly would put
 * the widget *inside* the matched node; `before` / `after` must be siblings.
 */
function createInjectedMountHost(target, placement, instanceId) {
  const host = document.createElement('div')
  host.className = 'ometrics-ai-inject-host'
  host.id = `ometrics-ai-${instanceId}`
  const parent = target.parentNode

  switch (placement) {
    case 'before':
      if (parent) {
        parent.insertBefore(host, target)
      } else {
        console.warn(
          '[OmetricsAI] injectionType "before" but selector node has no parent; appending inside target',
        )
        target.appendChild(host)
      }
      break
    case 'after':
      if (parent) {
        parent.insertBefore(host, target.nextSibling)
      } else {
        console.warn(
          '[OmetricsAI] injectionType "after" but selector node has no parent; appending inside target',
        )
        target.appendChild(host)
      }
      break
    case 'prepend':
      if (target.firstChild) {
        target.insertBefore(host, target.firstChild)
      } else {
        target.appendChild(host)
      }
      break
    default:
      target.appendChild(host)
  }
  prepareWidgetMountHost(host)
  return host
}

/**
 * True when the v2 widget config API says there is nothing to render for this
 * page (inactive agents, URL targeting miss, etc.). Preview mounts that pass
 * `data-agent-id` on the loader script bypass this check.
 *
 * @param {object} payload API JSON
 * @param {{ agentId?: string | null }} siteCfg loader script hints
 */
export function shouldSkipWidgetMountForPayload(payload, siteCfg = {}) {
  if (siteCfg.agentId) return false
  if (!payload || typeof payload !== 'object') return true

  const meta = payload.meta
  if (meta?.no_active_widget === true) return true
  if (meta?.empty === true) {
    const instances = Array.isArray(payload.instances) ? payload.instances : []
    if (instances.length === 0) return true
  }

  return false
}

/**
 * Resolves DOM hosts + merged per-mount config from a unified API payload.
 *
 * @param {object} payload API JSON
 * @param {{ agentId?: string | null, container?: string | null }} siteCfg loader script hints
 * @returns {{ node: Element, instanceId: string, config: object }[]}
 */
export function buildUnifiedMountQueue(payload, siteCfg = {}) {
  if (typeof document === 'undefined') return []

  if (shouldSkipWidgetMountForPayload(payload, siteCfg)) {
    return []
  }

  const instances = Array.isArray(payload?.instances) ? payload.instances : []

  const existing = document.querySelectorAll('[id^="ometrics-ai-"]')
  if (existing.length > 0) {
    return Array.from(existing).map((node) => {
      prepareWidgetMountHost(node)
      const instanceId = node.id.replace(/^ometrics-ai-/, '') || 'default'
      const inst =
        instances.find((s) => s && s.instanceId === instanceId) ?? null
      const config = inst
        ? mergeEnvelopeWithInstance(payload, inst)
        : { ...payload }
      return { node, instanceId, config }
    })
  }

  if (instances.length === 0) {
    const node = siteCfg.container
      ? document.querySelector(siteCfg.container)
      : null
    if (siteCfg.container && !node) {
      console.warn('[OmetricsAI] data-container selector matched no element')
      return []
    }
    const fallbackId = siteCfg.agentId || 'default'
    const host = node || createFloatingHost(fallbackId)
    const instanceId =
      (host.id && host.id.replace(/^ometrics-ai-/, '')) ||
      siteCfg.agentId ||
      'default'
    return [{ node: host, instanceId, config: { ...payload } }]
  }

  const queue = []
  for (let i = 0; i < instances.length; i += 1) {
    const inst = instances[i]
    if (!inst || typeof inst !== 'object') continue
    const instanceId = String(inst.instanceId || `${inst.kind || 'surface'}-${i}`)
    const merged = mergeEnvelopeWithInstance(payload, inst)

    if (inst.kind === 'injected' && inst.injectionSelector) {
      const target = document.querySelector(String(inst.injectionSelector))
      if (!target) {
        console.warn(
          `[OmetricsAI] injectionSelector matched no element: ${inst.injectionSelector}`,
        )
        continue
      }
      const placement = normalizeInjectionType(merged.injectionType)
      const host = createInjectedMountHost(target, placement, instanceId)
      queue.push({ node: host, instanceId, config: merged })
      continue
    }

    queue.push({
      node: createFloatingHost(instanceId),
      instanceId,
      config: merged,
    })
  }

  return queue
}
